import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/lib/ui/Typography';
import { Tabs } from '@/lib/ui/Tabs';
import { ForbiddenBanner } from '@/lib/ui/states';
import { useGate } from '@/lib/rbac/Gate';
import { SettingsTab } from './SettingsTab';
import { StringsTab } from './StringsTab';
import { NavigationTab } from './NavigationTab';
import { ContactDirectoryTab } from './ContactDirectoryTab';
import { CountriesTab } from './CountriesTab';
import { PageCopyTab } from './PageCopyTab';

/** Tab keys double as URL segments: `/settings/<tab>` (the default `settings` tab is `/settings`). */
const TAB_KEYS = [
  'settings',
  'strings',
  'navigation',
  'directory',
  'countries',
  'page-copy',
] as const;
type TabKey = (typeof TAB_KEYS)[number];
const DEFAULT_TAB: TabKey = 'settings';

function isTabKey(v: string | undefined): v is TabKey {
  return !!v && (TAB_KEYS as readonly string[]).includes(v);
}

/**
 * Site settings & chrome (spec §15.2). Admin-only; every write is audited + revalidated.
 *
 * Routing: tabs are URL-addressable via a splat route (`/settings/*`). Page copy supports a
 * second segment for the selected namespace (`/settings/page-copy/:namespace`) and a `?field=`
 * query for the field to focus — this is what the global search deep-links into.
 */
export function SettingsPage() {
  const { t } = useTranslation();
  const { allowed } = useGate('settings:manage');
  const params = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const segments = (params['*'] ?? '').split('/').filter(Boolean);
  const activeTab: TabKey = isTabKey(segments[0]) ? segments[0] : DEFAULT_TAB;
  const selectedNamespace =
    activeTab === 'page-copy' && segments[1] ? decodeURIComponent(segments[1]) : null;
  const focusField = activeTab === 'page-copy' ? searchParams.get('field') : null;
  // The locale the search hit matched in (`?loc=ar`), so deep-links land on the right-language
  // copy rather than its translation twin. Falls back to the UI locale when absent.
  const focusLocale =
    activeTab === 'page-copy' && searchParams.get('loc') === 'ar'
      ? 'ar'
      : activeTab === 'page-copy' && searchParams.get('loc') === 'en'
        ? 'en'
        : null;

  const goToTab = (value: string) =>
    navigate(value === DEFAULT_TAB ? '/settings' : `/settings/${value}`);

  const selectNamespace = (namespace: string | null) =>
    navigate(
      namespace ? `/settings/page-copy/${encodeURIComponent(namespace)}` : '/settings/page-copy',
    );

  return (
    <div>
      <PageHeader eyebrow={t('settings.eyebrow')} title={t('settings.pageTitle')} />
      {!allowed ? (
        <ForbiddenBanner message={t('settings.forbidden')} />
      ) : (
        <Tabs
          value={activeTab}
          onValueChange={goToTab}
          tabs={[
            { value: 'settings', label: t('settings.nav.settings'), content: <SettingsTab /> },
            { value: 'strings', label: t('settings.nav.strings'), content: <StringsTab /> },
            { value: 'navigation', label: t('settings.nav.navigation'), content: <NavigationTab /> },
            { value: 'directory', label: t('settings.nav.directory'), content: <ContactDirectoryTab /> },
            { value: 'countries', label: t('settings.nav.countries'), content: <CountriesTab /> },
            {
              value: 'page-copy',
              label: t('settings.nav.pageCopy'),
              content: (
                <PageCopyTab
                  selected={selectedNamespace}
                  focusField={focusField}
                  focusLocale={focusLocale}
                  onSelect={selectNamespace}
                />
              ),
            },
          ]}
        />
      )}
    </div>
  );
}
